import React, { useEffect, useState, useCallback } from 'react';
import { feedbackAPI } from '../../utils/api';

const STATUS_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'new', label: 'New' },
    { value: 'triaged', label: 'Triaged' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'spam', label: 'Spam' },
];

const CHANNEL_OPTIONS = [
    { value: '', label: 'All channels' },
    { value: 'text', label: 'Text' },
    { value: 'voice', label: 'Voice' },
];

function StatusPill({ status }) {
    const tone = {
        new:      'bg-blue-100  text-blue-800',
        triaged:  'bg-amber-100 text-amber-800',
        resolved: 'bg-emerald-100 text-emerald-800',
        spam:     'bg-gray-200  text-gray-600',
    }[status] || 'bg-gray-100 text-gray-700';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tone}`}>{status}</span>;
}

function TranscriptPill({ status }) {
    if (!status) return null;
    const map = {
        pending:  ['bg-yellow-100 text-yellow-800', 'transcribing'],
        done:     ['bg-emerald-100 text-emerald-800', 'transcript ready'],
        failed:   ['bg-red-100 text-red-700', 'transcript failed'],
        skipped:  ['bg-gray-100 text-gray-600', 'no transcript'],
    };
    const [tone, label] = map[status] || ['bg-gray-100 text-gray-600', status];
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${tone}`}>{label}</span>;
}

export default function FeedbackInboxPage() {
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState({});
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('new');
    const [channelFilter, setChannelFilter] = useState('');
    const [selected, setSelected] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        feedbackAPI.list({
            status: statusFilter,
            channel: channelFilter || undefined,
            limit: 100,
        })
            .then(res => {
                setRows(res.data?.rows || []);
                setTotals(res.data?.totals || {});
            })
            .catch(err => console.error('feedback list:', err))
            .finally(() => setLoading(false));
    }, [statusFilter, channelFilter]);

    useEffect(() => { load(); }, [load]);

    function openRow(row) {
        feedbackAPI.get(row.id)
            .then(res => setSelected(res.data))
            .catch(err => {
                console.error('feedback get:', err);
                setSelected(row);
            });
    }

    async function updateStatus(id, status) {
        try {
            const res = await feedbackAPI.update(id, { status });
            setSelected(res.data);
            load();
        } catch (err) { console.error('feedback patch:', err); }
    }

    async function saveNotes(id, admin_notes) {
        try {
            const res = await feedbackAPI.update(id, { admin_notes });
            setSelected(res.data);
        } catch (err) { console.error('feedback notes:', err); }
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Helper Genie — Feedback</h2>
                <button onClick={load} className="text-sm px-3 py-1 bg-gray-100 rounded hover:bg-gray-200">
                    Refresh
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-md p-4 mb-4 flex flex-wrap gap-3 items-center">
                <div className="flex gap-1">
                    {STATUS_OPTIONS.map(o => (
                        <button
                            key={o.value}
                            onClick={() => setStatusFilter(o.value)}
                            className={`px-3 py-1 rounded text-sm ${
                                statusFilter === o.value
                                    ? 'bg-zebcast-purple text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            {o.label}
                            {o.value !== 'all' && totals[`${o.value}_count`] != null && (
                                <span className="ml-1 text-xs opacity-75">({totals[`${o.value}_count`]})</span>
                            )}
                        </button>
                    ))}
                </div>
                <select
                    value={channelFilter}
                    onChange={(e) => setChannelFilter(e.target.value)}
                    className="border border-gray-300 rounded text-sm px-2 py-1"
                >
                    {CHANNEL_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <span className="ml-auto text-sm text-gray-500">
                    {totals.total != null ? `${totals.total} total` : ''}
                </span>
            </div>

            <div className="bg-white rounded-lg shadow-md">
                {loading && <div className="p-6 text-gray-400">Loading…</div>}
                {!loading && rows.length === 0 && (
                    <div className="p-10 text-center text-gray-400">
                        No feedback for this filter.
                    </div>
                )}
                {!loading && rows.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">When</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Channel</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reply email</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {rows.map(r => (
                                    <tr key={r.id}
                                        onClick={() => openRow(r)}
                                        className="hover:bg-gray-50 cursor-pointer">
                                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                                            {new Date(r.created_at + 'Z').toLocaleString()}
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            {r.channel === 'voice' ? '🎤 voice' : '✎ text'}
                                            {r.channel === 'voice' && (
                                                <div className="mt-0.5"><TranscriptPill status={r.transcript_status} /></div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700 max-w-md">
                                            <div className="line-clamp-2">
                                                {r.text_body || r.transcript ||
                                                    <span className="text-gray-400 italic">(audio only — open to listen)</span>}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                            {r.platform || '—'}
                                            {r.app_version && <span className="ml-1">{r.app_version}</span>}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-gray-700">
                                            {r.reply_email || <span className="text-gray-300">—</span>}
                                        </td>
                                        <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {selected && (
                <FeedbackDrawer
                    item={selected}
                    onClose={() => setSelected(null)}
                    onStatus={(s) => updateStatus(selected.id, s)}
                    onSaveNotes={(n) => saveNotes(selected.id, n)}
                />
            )}
        </div>
    );
}

function FeedbackDrawer({ item, onClose, onStatus, onSaveNotes }) {
    const [notes, setNotes] = useState(item.admin_notes || '');
    const [audioUrl, setAudioUrl] = useState('');
    const [audioErr, setAudioErr] = useState('');

    useEffect(() => { setNotes(item.admin_notes || ''); }, [item.id, item.admin_notes]);

    useEffect(() => {
        let revoke;
        if (item.channel === 'voice' && item.audio_filename) {
            feedbackAPI.fetchAudioBlob(item.id)
                .then(res => {
                    const url = URL.createObjectURL(res.data);
                    setAudioUrl(url);
                    revoke = url;
                })
                .catch(err => {
                    console.error('audio fetch:', err);
                    setAudioErr('Could not load audio file.');
                });
        }
        return () => { if (revoke) URL.revokeObjectURL(revoke); };
    }, [item.id, item.channel, item.audio_filename]);

    return (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={onClose}>
            <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-6 shadow-xl"
                 onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Feedback #{item.id}</h3>
                        <p className="text-xs text-gray-500">
                            {new Date(item.created_at + 'Z').toLocaleString()} ·
                            {' '}{item.platform || 'unknown'}
                            {item.app_version && <> · v{item.app_version}</>}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
                </div>

                <div className="mb-4 flex items-center gap-2">
                    <StatusPill status={item.status} />
                    <select
                        value={item.status}
                        onChange={(e) => onStatus(e.target.value)}
                        className="border border-gray-300 rounded text-sm px-2 py-1 ml-auto"
                    >
                        <option value="new">new</option>
                        <option value="triaged">triaged</option>
                        <option value="resolved">resolved</option>
                        <option value="spam">spam</option>
                    </select>
                </div>

                {item.channel === 'voice' && (
                    <div className="mb-4 border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-semibold">🎤 Voice clip</span>
                            <TranscriptPill status={item.transcript_status} />
                        </div>
                        {audioUrl ? (
                            <audio src={audioUrl} controls className="w-full" />
                        ) : audioErr ? (
                            <p className="text-sm text-red-600">{audioErr}</p>
                        ) : (
                            <p className="text-xs text-gray-400">Loading audio…</p>
                        )}
                        <p className="text-[11px] text-gray-500 mt-1">
                            {item.audio_duration_ms != null && `${(item.audio_duration_ms / 1000).toFixed(1)}s · `}
                            {item.audio_bytes != null && `${(item.audio_bytes / 1024).toFixed(0)} KB · `}
                            {item.audio_mime}
                        </p>
                        {item.transcript && (
                            <div className="mt-3 bg-gray-50 rounded p-3 text-sm text-gray-800 whitespace-pre-wrap">
                                {item.transcript}
                            </div>
                        )}
                    </div>
                )}

                {item.text_body && (
                    <div className="mb-4">
                        <p className="text-xs font-semibold text-gray-500 mb-1">Message</p>
                        <div className="bg-gray-50 rounded p-3 text-sm text-gray-800 whitespace-pre-wrap">
                            {item.text_body}
                        </div>
                    </div>
                )}

                <div className="mb-4 grid grid-cols-2 gap-3 text-xs">
                    <Meta label="Reply to" value={item.reply_email} mailto />
                    <Meta label="Device ID" value={item.device_id} />
                    <Meta label="Locale" value={item.locale} />
                    <Meta label="Page" value={item.page_url} />
                    <Meta label="IP hash" value={item.ip_hash} />
                    <Meta label="User agent" value={item.user_agent} wrap />
                </div>

                <div className="mb-4">
                    <p className="text-xs font-semibold text-gray-500 mb-1">Internal notes</p>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={4}
                        className="w-full border border-gray-300 rounded p-2 text-sm"
                    />
                    <button
                        onClick={() => onSaveNotes(notes)}
                        className="mt-2 px-3 py-1 rounded bg-zebcast-purple text-white text-sm"
                        style={{ background: '#7c3aed' }}
                    >
                        Save notes
                    </button>
                </div>

                {item.reply_email && (
                    <a
                        href={`mailto:${item.reply_email}?subject=Re: your ZebCast feedback (%23${item.id})`}
                        className="block w-full text-center py-2 rounded bg-gray-900 text-white text-sm font-semibold"
                    >
                        Reply by email
                    </a>
                )}
            </div>
        </div>
    );
}

function Meta({ label, value, mailto, wrap }) {
    if (!value) return null;
    return (
        <div>
            <p className="text-gray-500">{label}</p>
            {mailto
                ? <a href={`mailto:${value}`} className="text-zebcast-purple break-all">{value}</a>
                : <p className={`text-gray-800 ${wrap ? 'break-all' : 'truncate'}`}>{value}</p>}
        </div>
    );
}
